import { useEffect, useRef, useState } from 'react';
import { ADS_APP_ID, ADS_ENABLED, ADS_FRAME_ORIGIN, ADS_MANAGER_ORIGIN, ADS_MIN_WIDTH } from './config';
import { getConsent, setConsent, subscribeConsent } from './consent';
import { loadAdSdk } from './sdk';
import type { AdController } from './types';
import { PROFILE_LINKS, safeWebLink } from '../config/header';

const preferenceKey = 'numbernode:offline-sponsorship:v1';
const readOfflinePreference = () => { try { return localStorage.getItem(preferenceKey) === '1'; } catch { return false; } };

export function AdRail({ localOnly }: { localOnly: boolean }) {
  const host = useRef<HTMLElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const [offline, setOffline] = useState(readOfflinePreference);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [sdkFailed, setSdkFailed] = useState(false);
  const networkBlocked = localOnly || offline;

  useEffect(() => {
    function preferences(event?: Event) {
      // A real CMP can preventDefault on this event and display its own interface.
      event?.preventDefault();
      const custom = new CustomEvent('numbernode:open-ad-preferences', { cancelable: true });
      if (document.dispatchEvent(custom)) setPreferencesOpen(true);
    }
    function consent(event: Event) { setConsent((event as CustomEvent).detail); }
    function storage(event: StorageEvent) { if (event.key === preferenceKey || event.key === null) setOffline(readOfflinePreference()); }
    const api = { setConsent, openPreferences: () => preferences() };
    window.NumberNodeAds = api;
    document.addEventListener('picplayer:ad-preferences', preferences);
    document.addEventListener('numbernode:ad-consent', consent);
    window.addEventListener('storage', storage);
    return () => {
      if (window.NumberNodeAds === api) delete window.NumberNodeAds;
      document.removeEventListener('picplayer:ad-preferences', preferences);
      document.removeEventListener('numbernode:ad-consent', consent);
      window.removeEventListener('storage', storage);
    };
  }, []);

  useEffect(() => {
    const element = host.current;
    if (!ADS_ENABLED || !element) return;
    let cancelled = false;
    let controller: AdController | undefined;
    let unsubscribe: (() => void) | undefined;
    element.hidden = false;
    setSdkFailed(false);
    void loadAdSdk().then((sdk) => {
      if (cancelled) return;
      controller = sdk.mount({
        element,
        appId: ADS_APP_ID,
        managerUrl: ADS_MANAGER_ORIGIN,
        frameOrigin: ADS_FRAME_ORIGIN,
        cssUrl: '/ads/rail.css',
        privacyUrl: safeWebLink(PROFILE_LINKS.privacy, '/privacy.html'),
        privacyText: 'Advertising helps support our tools. Your datasets, SQL queries and project names are never sent to the ad manager.',
        localOnly: networkBlocked,
        localMessage: localOnly ? 'Local data.\nMore possibility.' : 'A little space.\nMore possibility.',
        minViewportWidth: ADS_MIN_WIDTH,
        consent: networkBlocked ? { ads: false, networkIds: [] } : getConsent(),
      });
      unsubscribe = subscribeConsent((value) => controller?.setConsent(networkBlocked ? { ads: false, networkIds: [] } : value));
    }).catch(() => { if (!cancelled) setSdkFailed(true); });
    return () => { cancelled = true; unsubscribe?.(); controller?.destroy(); };
  }, [networkBlocked, localOnly]);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (preferencesOpen && !element.open) element.showModal();
    else if (!preferencesOpen && element.open) element.close();
  }, [preferencesOpen]);

  function updateOffline(value: boolean) {
    setOffline(value);
    try { localStorage.setItem(preferenceKey, value ? '1' : '0'); } catch { /* Session-only when storage is unavailable. */ }
    if (value) setConsent({ ads: false, networkIds: [] });
  }

  return <>
    {ADS_ENABLED && <aside ref={host} className="numbernode-ad-rail" data-numbernode-ad-app={ADS_APP_ID} aria-label="Sponsored advertisements">
      <div className="ads-loading"><strong>SPONSORED</strong><p>{sdkFailed ? 'Sponsorship is unavailable. Your local workspace is unaffected.' : 'A little space. More possibility.'}</p><button type="button" onClick={() => setPreferencesOpen(true)}>Ad choices</button></div>
    </aside>}
    <dialog ref={dialog} className="ad-preferences-dialog" aria-labelledby="ad-preferences-title" onCancel={() => setPreferencesOpen(false)} onClose={() => setPreferencesOpen(false)}>
      <h2 id="ad-preferences-title">Ad choices</h2>
      <p>{localOnly ? 'Local only is enabled. The sidebar shows local sponsorship content and makes no requests to the ad manager.' : 'When remote connections are allowed, contextual advertisements can be requested from PicPlayer’s shared ad manager.'}</p>
      <p>Datasets, file contents, SQL queries and project names are not included in advertising requests. Remote ad requests still disclose network information, including your IP address and this app’s origin, to the service.</p>
      <label><input type="checkbox" checked={offline} onChange={(event) => updateOffline(event.target.checked)} /><span>Keep advertising offline, even when remote data connections are allowed.</span></label>
      <p>Only contextual text ads are eligible by default. Consent-dependent networks require permission from a connected consent-management platform; this panel does not grant that permission.</p>
      <a href={safeWebLink(PROFILE_LINKS.privacy, '/privacy.html')} target="_blank" rel="noopener noreferrer">Read the privacy notice</a>
      <div className="ad-preferences-actions"><button type="button" className="secondary" onClick={() => setConsent({ ads: false, networkIds: [] })}>Withdraw network consent</button><button type="button" className="primary" onClick={() => setPreferencesOpen(false)}>Done</button></div>
    </dialog>
  </>;
}
